'use client';

import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

import { RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';

/**
 * 사이드바 필터용 아코디언 섹션 (헤더 + pill 요약 + 쉐브론)
 */
/**
 * 필터 패널의 **접이 구획** 하나 — 제목 · 개수 배지 · 펼침 토글.
 *
 * ⚠️ **JSDoc이 계약이다** — `.tsx` 소비자의 호출부 타입이 여기서 나온다.
 * 기본값이 없어 전부 **필수**로 좁혀지므로 적어 둔다.
 *
 * ⚠️ 펼침 상태를 **자기가 갖지 않는다**(`isOpen`·`onToggle`). 여러 구획 중 하나만
 * 열어 두는 규칙을 부모가 정할 수 있어야 하기 때문이다.
 */
type FilterAccordionSectionProps = {
  title: ReactNode;
  /** 제목 옆 개수 배지 */
  countLabel?: ReactNode;
  isOpen: boolean;
  /**
   * ⚠️ 헤더의 `onClick` 으로 그대로 넘어가므로 **이벤트를 받는다.** 안 주면 접기가 안 될 뿐
   * 깨지지는 않는다 — 실제로 `undefined` 를 넘기는 소비자가 있다(filter-sort-section)
   */
  onToggle?: (event?: unknown) => void;
  /**
   * 제목 줄 오른쪽(쉐브론 앞)의 작은 글자 단추 — 「전체 선택/해제」 토글 하나.
   * ⚠️ 누르기가 접기로 새지 않게 이 부품이 전파를 막는다.
   */
  headerAction?: { label: string; pressed: boolean; onClick: () => void } | null;
  children: ReactNode;
};

export function FilterAccordionSection({
  title,
  countLabel,
  isOpen,
  onToggle,
  headerAction = null,
  children,
}: FilterAccordionSectionProps) {
  const theme = useTheme();

  return (
    <Box
      component="section"
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            // JSDoc 상 선택인데 무가드 호출이던 자리 — 동작 보존을 위해 단언만 얹었다
            onToggle!(e);
          }
        }}
        role="button"
        tabIndex={0}
        sx={{
          cursor: 'pointer',
          py: 1.5,
          px: 1.5,
          borderRadius: 0,
          outline: 'none',
          '&:hover': {
            bgcolor: alpha(theme.palette.grey[500], 0.06),
          },
          '&:focus-visible': {
            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.35)}`,
          },
        }}
      >
        <Typography
          component="span"
          sx={{
            flex: '1 0 auto',
            whiteSpace: 'nowrap',
            typography: 'uiTitle',
            lineHeight: 1.35,
            color: 'text.primary',
            letterSpacing: '-0.02em',
          }}
        >
          {title}
        </Typography>
        {countLabel != null && countLabel !== '' && (
          <Typography
            component="span"
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              typography: 'uiBody',
              color: 'text.secondary',
              lineHeight: 1.3,
            }}
          >
            {countLabel}
          </Typography>
        )}
        {headerAction && (
          <ButtonBase
            aria-pressed={headerAction.pressed}
            onClick={(event) => {
              event.stopPropagation();
              headerAction.onClick();
            }}
            onKeyDown={(event) => event.stopPropagation()}
            sx={{ flexShrink: 0, px: 0.5, borderRadius: RADIUS.sm, typography: 'uiAction', color: 'primary.main' }}
          >
            {headerAction.label}
          </ButtonBase>
        )}
        <Iconify
          icon="solar:alt-arrow-down-linear"
          width={18}
          sx={{
            color: 'grey.500',
            flexShrink: 0,
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </Stack>
      <Collapse in={isOpen}>
        <Box sx={{ mx: 1, pb: 1.5, pt: 0 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}
